import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import { IUser } from "../../user/model/User";
import { IOrganisation } from "../../organisations/model/Organisation";
import { IOrganisationIndexCategory } from "../../organisations/model/OrganisationIndexCategory";
import GetIndexesByCategoryIndexDataInteractor from "../interactor/GetIndexesByCategoryIndexDataInteractor";
import GetAllOrganisationIndexCategoriesInteractor from "../../organisations/interactors/GetAllOrganisationIndexCategoriesInteractor";
import { IIndexHarmonizationReportItem } from "../model/IndexHarmonizationReportItem";

export interface TIndexesByOrganisationDataReportPresenter
  extends TLoadingAwarePresenter {
  user?: IUser;
  organisationId: number;
  organisations: IOrganisation[];
  organisationIndexCategories: IOrganisationIndexCategory[];
  dateIncluded?: Date;
  selectedCategoryId: number;
  data: IIndexHarmonizationReportItem[];
}

export interface IIndexesByOrganisationDataReportPresenter
  extends TIndexesByOrganisationDataReportPresenter,
    TPresentable {
  loadReferenceValues(organisations: IOrganisation[]): void;
  setOrganisationId(organisationId: number): void;
  setDateIncluded(dateIncluded: Date): void;
  setOrganisationIds(organisationIds: number[]): void;
  setCategoryId(categoryId: number): void;
  getData(): void;
}

const defaultState: TIndexesByOrganisationDataReportPresenter = {
  organisationId: NaN,
  selectedCategoryId: NaN,
  organisations: [],
  organisationIndexCategories: [],
  data: []
};

const IndexesByOrganisationDataReportPresenter = withStore<
  IIndexesByOrganisationDataReportPresenter,
  TIndexesByOrganisationDataReportPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IIndexesByOrganisationDataReportPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<
      TIndexesByOrganisationDataReportPresenter & TLoadingAwarePresenter
    >();
    loader.start("projectLoader");
    const loadReferenceValues = (organisations: IOrganisation[]) => {
      const user = _store.getState<any>().user;
      _store.update({ organisations, user });
      user.organisationId && setOrganisationId(user.organisationId);
      loader.stop("projectLoader");
      loader.stop("reportLoader");
    };
    const setOrganisationId = async (organisationId: number) => {
      if (organisationId) {
        getOrganisationIndexCategories(organisationId);
      }
      _store.update({ organisationId: organisationId, data: undefined });
    };
    const getOrganisationIndexCategories = async (organisationId: number) => {
      let data = await _application.container
        .resolve<GetAllOrganisationIndexCategoriesInteractor>(
          "getAllOrganisationIndexCategories"
        )
        .execute(organisationId);
      _store.update({
        organisationIndexCategories: data,
        selectedCategoryId: undefined,
        data: undefined
      });
    };
    const setDateIncluded = (dateIncluded: Date) => {
      _store.update({ dateIncluded, data: undefined });
    };
    const setCategoryId = (categoryId: number) => {
      _store.update({ selectedCategoryId: categoryId, data: undefined });
    };
    const setOrganisationIds = (organisationIds: number[]) => {
      _store.update({ organisationIds, data: undefined });
    };
    const getData = async () => {
      const { organisationId, selectedCategoryId } =
        _store.getState<TIndexesByOrganisationDataReportPresenter>();
      loader.start("reportLoader");
      let data = await _application.container
        .resolve<GetIndexesByCategoryIndexDataInteractor>(
          "getIndexesByCategoryIndexData"
        )
        .execute(organisationId, selectedCategoryId);
      _store.update({ data });
      loader.stop("reportLoader");
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      setOrganisationId,
      setDateIncluded,
      setOrganisationIds,
      getData,
      setCategoryId
    };
  },
  defaultState
);

export default IndexesByOrganisationDataReportPresenter;
